import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, IconButton, Toolbar, Slide } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions';
import CheckDetails from './CheckDetails';
import { String_ViewCheckDialog_Title, String_ViewCheckDialog_CheckNumberLabel, String_System_GeneralError, 
    String_System_ConnectionError, String_ViewCheckDialog_PayButton } from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from './ErrorDialog';
import Loader from './Loader';
import { ElementDimensions } from '../../styles/ElementDimensions'
import { Skin } from '../../models/common/Skin';
import { CheckSummary } from '../../models/common/CheckSummary';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from './Buttons/FullWidthButton';
import { GetCheckRequest } from '../../models/checkout/GetCheckRequest';
import { GetCheckResponse } from '../../models/checkout/GetCheckResponse';
import { CheckFlat } from '../../models/common/CheckFlat';
import { CheckState as CheckStateEnum } from '../../models/common/CheckState';
import { POSType } from '../../models/common/POSType';

const useStyles = makeStyles({
    appBar: {
        height: ElementDimensions.dialogBarHeight + 'px',
        position: 'relative',
        backgroundColor: props => (props as ViewCheckDialogStyleProps).skin.primary_main_color + ' !important',
    },
    checkDetailsContainer: {
        backgroundColor: themePrimary.palette.grey[100],
        borderRadius: '6px',
        marginLeft:  ElementDimensions.margin10 + 'px',
        marginRight:  ElementDimensions.margin10 + 'px',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    details: {
        position: 'absolute',
        top: ElementDimensions.dialogBarHeight + 'px',
        bottom:  props => (props as ViewCheckDialogStyleProps).showFooter ? ElementDimensions.footerBarHeight + 'px' : '0px',
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    dialog: {
        backgroundColor: themePrimary.palette.secondary.light,
    },
    dialogLandscape: {
        backgroundColor: themePrimary.palette.secondary.light,
        left: 'calc(100vw *.3) !important',
        width: 'calc(100vw *.7)',
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    headerContainer: {
        padding: ElementDimensions.padding10 + 'px',
        width: '100%',
    },
    headerTextLeft: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    logoContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        textAlign: 'center',
        paddingTop: ElementDimensions.padding10 + 'px',
        paddingBottom: ElementDimensions.padding10 + 'px',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
});

export type ViewCheckDialogCloseClickHandler = (event: React.MouseEvent<HTMLElement>) => any;
export type ViewCheckPayClickHandler = (event: React.MouseEvent<HTMLElement>, check: CheckSummary) => any;

export type ViewCheckDialogStyleProps = {
    skin: Skin;
    showFooter: boolean;
}

export interface ViewCheckDialogProps {
    checkSummary: CheckSummary | null;
    checkDetail: CheckFlat | null;
    open: boolean;
    showFooter: boolean;
    useClaimedAmounts: boolean;
    onPayClickHandler: ViewCheckPayClickHandler,
    onCloseHandler: ViewCheckDialogCloseClickHandler;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViewCheckDialog = (props: ViewCheckDialogProps) => {
    const { checkSummary, checkDetail, open, showFooter, useClaimedAmounts, onPayClickHandler, onCloseHandler } = props;
    const isPortrait = true;
    const [check, setCheck] = React.useState<CheckFlat | null>(null);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [buttonClickLock, setButtonClickLock] = React.useState<boolean>(false);
    const [noUnclaimedAmountsError, setNoUnclaimedAmountsError] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ skin: { primary_main_color: checkOutState.state.skin.primary_main_color, 
      primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color },
        showFooter: showFooter });

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        if (open) {
            loadData(source);
        }
                
        return () => {
            source.cancel();
        };
    }, [open]);

    const loadData = async (cancelTokenSource: CancelTokenSource) => {

        if (checkSummary !== null && checkSummary !== undefined) {
            if (checkDetail !== null) {
                if (checkSummary.check_state !== CheckStateEnum.Paid) {
                    if (checkSummary.unclaimed_amount === 0) {
                        setNoUnclaimedAmountsError(true);
                    }
                    else {
                        setNoUnclaimedAmountsError(false);
                    }
                }
                else {
                    setNoUnclaimedAmountsError(true);
                }

                setCheck(checkDetail);
                setLoaded(true);
            }
            else {
                setCheck(null);
                setLoaded(false);
                setLoadError(false);
                setErrorDialogOpen(false);

                const request: GetCheckRequest = {
                    user_id: userState.state.userId!!,
                    use_claimed_amounts: true,
                    release_claims: false,
                };        
        
                let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkSummary.id
        
                const headers = {
                    'X-API-Key': checkOutState.state.restaurantApiKey,
                    'location_id': checkOutState.state.restaurantId
                }
        
                axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
                    .then(function(response) {
                        let r = response.data as GetCheckResponse;

                        if (r.success === true) {
                            if (r.summary.check_state !== CheckStateEnum.Paid) {
                                if (r.summary.unclaimed_amount === 0) {
                                    setNoUnclaimedAmountsError(true);
                                }
                                else {
                                    setNoUnclaimedAmountsError(false);
                                }
                            }
                            else {
                                setNoUnclaimedAmountsError(true);
                            }

                            setCheck(r.detail);
                        }
                        else {
                            setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                            setErrorDialogOpen(true);
                            setLoadError(true);
                        }

                        setLoaded(true);
                    })
                    .catch(function(error) {
                        if (error.response) {
                            setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                        }
                        else {
                            setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                        }
        
                        setErrorDialogOpen(true);
                        setLoaded(true);
                        setLoadError(true);
                    });
            }
        }
    }

    const handlePayButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!buttonClickLock) {
            setButtonClickLock(true);
            if (check !== null && check !== undefined) {
                onPayClickHandler(event, checkSummary!!);
                setButtonClickLock(false);
            }
        }
    }

    const buttonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_ViewCheckDialog_PayButton.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handlePayButtonClick,
    }

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        onCloseHandler(event);
        setErrorDialogOpen(false);
        setErrorMessage('');
    };

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: true,
        onCloseHandler: handleErrorDialogClose,
    }

    return (
        <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition} className={isPortrait === true ? classes.dialog : classes.dialogLandscape}>
            
                <div>
                    
                    <AppBar className={classes.appBar}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                                <CloseIcon className={classes.closeButton}/>
                            </IconButton>
                            <Typography variant="h6" className={classes.title}>
                                {String_ViewCheckDialog_Title.find(s => s.key === userState.state.locale)!!.value}
                            </Typography>
                        </Toolbar>
                    </AppBar>

                    {!loaded && !loadError &&
                        <div className={classes.loadingContainer}>
                            {!loaded &&
                                <Loader />                    
                            }
                        </div>
                    }

                    {loaded && !loadError && check !== null && check !== undefined &&
                        <div className={classes.details}>
                            <div className={classes.headerContainer}>
                                <Typography variant="h6" className={classes.headerTextLeft}>
                                    {checkOutState.state.posType !== POSType.Clear &&
                                        String_ViewCheckDialog_CheckNumberLabel.find(s => s.key === userState.state.locale)!!.value
                                    }
                                    {checkOutState.state.posType !== POSType.Clear &&
                                        " " + check.checkNumber
                                    }
                                    {checkOutState.state.posType == POSType.Clear &&
                                        " " + checkSummary!!.number
                                    }
                                </Typography>
                            </div>

                            {loaded && !loadError && check !== null && check !== undefined &&
                                <div className={classes.checkDetailsContainer}>
                                    <CheckDetails details={check.details} isReceipt={true}/>
                                </div>
                            }
                        </div>
                    }

                    {loaded && !loadError && check !== null && check !== undefined && showFooter && !noUnclaimedAmountsError &&
                        <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                            <FullWidthButton 
                                disabled={buttonProps.disabled}
                                label={buttonProps.label}
                                alternateColor={buttonProps.alternateColor}
                                size={buttonProps.size}
                                onClickHandler={buttonProps.onClickHandler}/>
                        </div>
                    }

                </div>

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </Dialog>
    );
};

export default ViewCheckDialog;